import { Dialog } from '../components/FarChat/ChatFeedbackOverlays';
import React, { useState } from 'react';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import type { ConstructionTimingRequirement } from '../api/constructionPriceEstimatorFunctions';

interface Props {
  items?: ConstructionTimingRequirement[];
  canEdit: boolean;
  onChange: (items: ConstructionTimingRequirement[]) => void;
  sourceStatus?: 'complete' | 'unreadable' | 'not_available';
  stale?: boolean;
}

const unitLabels = {
  calendar_days: 'calendar days', workdays: 'workdays', unspecified_days: 'days (day type not stated)',
  weeks: 'weeks', months: 'months', hours: 'hours', years: 'years',
};

const timingLabel = (item: ConstructionTimingRequirement): string => {
  if (item.strength === 'planning_target') return 'Planning target';
  if (item.kind === 'work_access_restriction') return 'Work restriction';
  if (item.kind === 'overall_performance_period' && item.strength === 'required') return 'Required completion';
  const labels = {
    overall_performance_period: 'Overall performance period', phase_duration: 'Phase duration',
    milestone_deadline: 'Milestone / deadline', notice: 'Advance notice', review: 'Review window',
    delivery: 'Delivery window', qualitative_dependency: 'Sequencing condition',
  };
  return labels[item.kind as keyof typeof labels] ?? 'Timing constraint';
};

/** Timing is source evidence, not a forecast or an input to staffing/cost arithmetic. */
export default function TimingRequirements({ items = [], canEdit, onChange, sourceStatus, stale = false }: Props) {
  const [source, setSource] = useState<ConstructionTimingRequirement | null>(null);
  const orderedItems = items.filter(item => item.kind === 'overall_performance_period')
    .concat(items.filter(item => item.kind !== 'overall_performance_period'));
  return (
    <Box component="section" aria-labelledby="timing-requirements-heading" sx={{ mt: 1, mb: 1 }}>
      <Typography id="timing-requirements-heading" variant="h6" component="h3">Timing requirements</Typography>
      {!items.some(item => item.kind === 'overall_performance_period') && <Typography variant="body2" sx={{ mt: 1 }}>Overall completion: {sourceStatus === 'unreadable' ? 'Unavailable' : 'Not stated'}</Typography>}
      {sourceStatus === 'unreadable' && <Typography variant="body2" color="text.secondary">Timing extraction is incomplete. No duration was inferred.</Typography>}
      {stale && <Typography variant="body2" color="warning.main">Saved timing may be out of date. Fill contract details to refresh it.</Typography>}
      <Stack spacing={1.5} sx={{ mt: items.length ? 1 : 0 }}>
        {orderedItems.map(item => (
          <Box key={item.id} sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
            <Typography variant="body2" fontWeight={600}>{stale && item.source_id.trim() && item.evidence_quote.trim() ? `Saved ${timingLabel(item).toLowerCase()}` : timingLabel(item)}</Typography>
            <Typography variant="body2">{item.label}</Typography>
            {item.value && <Typography variant="body2">{item.value}{item.value_max ? `–${item.value_max}` : ''}{item.unit ? ` ${unitLabels[item.unit]}` : ' (unit not stated)'}</Typography>}
            <Typography variant="body2" color="text.secondary">Start trigger: {item.trigger || 'Not stated'}</Typography>
            {item.phase && <Typography variant="body2">Phase: {item.phase}</Typography>}
            {item.start_date && <Typography variant="body2">Stated start: {item.start_date}</Typography>}
            {item.end_date && <Typography variant="body2">Stated end: {item.end_date}</Typography>}
            {item.conditions.map((condition, index) => <Typography key={index} variant="body2">{condition}</Typography>)}
            {item.discrepancy && <Typography variant="body2" color="warning.main">{item.discrepancy}</Typography>}
            <Button size="small" onClick={() => setSource(item)} aria-label={`View timing source: ${item.label}`} sx={{ px: 0 }}>View source</Button>
            {item.owner_override?.description && <Typography variant="body2">User-edited timing: {item.owner_override.description}</Typography>}
            {canEdit && <Box sx={{ mt: 0.5, maxWidth: 760 }}>
              <Typography component="label" htmlFor={`timing-edit-${item.id}`} variant="body2" sx={{ display: 'block', mb: 0.5 }}>Edited timing (optional)</Typography>
              <TextField id={`timing-edit-${item.id}`} fullWidth size="small" inputProps={{ 'aria-label': `Edited timing: ${item.label}`, maxLength: 1500 }} value={item.owner_override?.description ?? ''}
                onChange={event => onChange(items.map(current => current.id === item.id ? { ...current, owner_override: event.target.value.trim() ? { description: event.target.value } : null } : current))} />
            </Box>}
          </Box>
        ))}
      </Stack>
      <Dialog open={Boolean(source)} onClose={() => setSource(null)} maxWidth="sm" fullWidth aria-labelledby="timing-source-heading">
        <DialogTitle id="timing-source-heading">Timing source</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{source?.source_kind === 'project_brief' ? 'User-stated project description' : source?.document_name || 'Statement of Work'}</Typography>
          <Typography variant="body2" color="text.secondary">{source?.source_locator || 'Source location not stated'}</Typography>
          <Typography component="blockquote" variant="body2" sx={{ mx: 0, whiteSpace: 'pre-wrap' }}>{source?.evidence_quote}</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setSource(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
